// Browser-side fetch wrappers around /api/comments. The Worker proxies to
// GitHub Issues; reviewers never authenticate.

import type { CommentResponse, Reply } from './types';

const BASE = '/api/comments';

async function unwrap<T>(res: Response): Promise<T> {
  if (!res.ok) {
    // Read raw text first, then attempt JSON parse — calling res.json()
    // followed by res.text() trips "Body is disturbed or locked" because
    // the failed json() still consumes the body. The non-OK path is also
    // where `npm run dev` lands when the Worker isn't running locally
    // (Vite returns the SPA index.html for /api/comments, which isn't JSON).
    const text = await res.text();
    let detail: unknown = text;
    try {
      detail = JSON.parse(text);
    } catch {
      // leave as raw text
    }
    const message =
      typeof detail === 'string' ? detail : JSON.stringify(detail);
    throw new Error(`API ${res.status}: ${message}`);
  }
  return (await res.json()) as T;
}

export async function listComments(
  version: string | 'all',
  state: 'open' | 'closed' | 'all' = 'open',
): Promise<CommentResponse[]> {
  const res = await fetch(`${BASE}?version=${encodeURIComponent(version)}&state=${state}`);
  return unwrap<CommentResponse[]>(res);
}

export type CreatePayload = {
  version: string;
  component: string;
  route: string;
  selector?: string;
  elementTag?: string;
  elementText?: string;
  elementAriaLabel?: string | null;
  offsetX: number;
  offsetY: number;
  viewport: [number, number];
  author: string;
  body: string;
  // Optional click-area screenshot URL (already uploaded via
  // `uploadScreenshot()` below). Server prepends this as inline Markdown to
  // the GitHub issue body when present.
  screenshotUrl?: string;
};

// Upload a captured PNG to the Worker's R2-backed endpoint. Returns the
// public URL of the stored screenshot. Intentionally lenient — callers must
// treat any throw as recoverable and submit the comment without a URL.
export async function uploadScreenshot(blob: Blob): Promise<string> {
  const res = await fetch('/api/comments/screenshot', {
    method: 'POST',
    headers: { 'Content-Type': 'image/png' },
    body: blob,
  });
  const parsed = await unwrap<{ url: string }>(res);
  return parsed.url;
}

export async function createComment(payload: CreatePayload): Promise<CommentResponse> {
  const res = await fetch(BASE, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return unwrap<CommentResponse>(res);
}

export async function listReplies(number: number): Promise<Reply[]> {
  const res = await fetch(`${BASE}/${number}/replies`);
  return unwrap<Reply[]>(res);
}

export async function postReply(
  number: number,
  author: string,
  body: string,
): Promise<{ id: number; createdAt: string }> {
  const res = await fetch(`${BASE}/${number}/replies`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ author, body }),
  });
  return unwrap(res);
}

export async function updateStatus(
  number: number,
  status: 'resolved' | 'reopen',
): Promise<void> {
  const res = await fetch(`${BASE}/${number}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status }),
  });
  await unwrap(res);
}

export async function deleteComment(number: number, author: string): Promise<void> {
  const res = await fetch(`${BASE}/${number}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ author }),
  });
  // DELETE returns 204 (no body); only unwrap on non-2xx.
  if (!res.ok) {
    let detail: unknown;
    try {
      detail = await res.json();
    } catch {
      detail = await res.text();
    }
    throw new Error(`API ${res.status}: ${JSON.stringify(detail)}`);
  }
}
